/*
 * VERIFY NEW EMAIL (decision 608): the page the NEW address's link opens.
 *
 * TWO BUTTONS AND A SENTENCE (Kevin's review): Update moves the account to the
 * new address, Cancel declines it and keeps the old one. A press, not an
 * on-mount POST, because this is the one link whose state change is a CHOICE:
 * the holder of the new inbox may be declining a change somebody else asked
 * for, and a page that changed the address as it loaded would take that choice
 * away.
 *
 * Either press goes straight home, with no window in between. Sessions are
 * kept, so a signed-in device stays signed in; its copy of the user is
 * refreshed first so the app shows the new address at once.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Brand } from "../components/Brand";
import { ApiError, api, isConflict, type User } from "../lib/api";
import { useLinkToken } from "../lib/linkToken";
import styles from "./SignIn.module.css";

export function VerifyNewEmail({
  signedIn,
  onUserChanged,
}: {
  signedIn: boolean;
  onUserChanged: (user: User | null) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useLinkToken();
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<"ready" | "invalid" | "taken" | "failed">(
    token === null ? "invalid" : "ready",
  );

  async function goHome() {
    if (signedIn) {
      const me = await api.me().catch(() => null);
      if (me?.user) onUserChanged(me.user);
    }
    navigate("/", { replace: true });
  }

  async function update() {
    if (!token) return;
    setBusy(true);
    try {
      await api.confirmEmailChange(token);
      await goHome();
    } catch (err) {
      // 409 arrives as the client's `onboarding-incomplete` kind, so it is read
      // through isConflict in this endpoint's own meaning: the address was
      // taken by another account after the link went out.
      if (isConflict(err)) setState("taken");
      else if (err instanceof ApiError && err.failure.kind === "server" && err.failure.status === 400)
        setState("invalid");
      else setState("failed");
      setBusy(false);
    }
  }

  async function cancel() {
    // Best effort: a link that is already dead is declined either way, so a
    // failure here still goes home with the old address in place.
    if (token) await api.cancelEmailChangeByToken(token).catch(() => undefined);
    await goHome();
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Brand variant="lockup" height={34} />
        </div>
        <h1 className={styles.heading}>{t("auth.verifyNewEmail.heading")}</h1>

        {state === "invalid" ? (
          <>
            <p className={styles.intro}>{t("auth.linkInvalid")}</p>
            <p className={styles.intro}>{t("auth.verifyNewEmail.askAgain")}</p>
          </>
        ) : state === "taken" ? (
          <p className={styles.intro}>{t("auth.verifyNewEmail.taken")}</p>
        ) : (
          <>
            <p className={styles.intro}>{t("auth.verifyNewEmail.intro")}</p>
            {state === "failed" ? (
              <p className={styles.error} role="alert">
                {t("auth.errorGeneric")}
              </p>
            ) : null}
            <button className={styles.primary} type="button" disabled={busy} onClick={() => void update()}>
              {t("auth.verifyNewEmail.update")}
            </button>
            <button className={styles.outline} type="button" disabled={busy} onClick={() => void cancel()}>
              {t("auth.verifyNewEmail.cancel")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
